import { useEffect, useRef, useState } from "react";

// Director talkback (ADR 0213, LSC-423) — a PRIVATE Director → talent audio path
// (push-to-talk) that is NEVER part of the Fan/Program mix or the program recording.
//
// Routing model. The Director publishes a dedicated microphone track named
// `talkback` and restricts it, at the SFU, to ONLY the targeted talent via
// LocalParticipant.setTrackSubscriptionPermissions(false, [target…]). That gives a
// hard, SFU-enforced guarantee on the paths that matter:
//   • Fans never receive it (not permitted) — and even if they did, ConsumerWatch
//     only ever attaches the on-program SOURCE's audio, and the Director is never a
//     program source, so a talkback track has no fan playback path (defence in depth).
//   • The room-composite / program-render egress recorder is a SUBSCRIBER, so it is
//     excluded by the same permission; the program-render template also only plays
//     program audio. So the produced recording never carries talkback.
// The one residual is LEGACY ISO participant-egress, which records each publisher's
// own tracks server-side (bypassing subscription permissions). That is a documented
// follow-up (ADR 0213 / LSC-471); talkback is momentary PTT and program-render VOD is
// the safe intended default.
//
// This module holds the PURE, unit-tested pieces — the target model, the permission
// list, and talkback-track identification — plus small helpers the consoles call.
// The live LiveKit calls (publish/mute/permissions/attach) are best-effort wrapped in
// the consoles, and the real audio is beta QA (jsdom can't run WebRTC audio).

/** The published track name that marks a Director talkback mic (how talent tell it
 *  apart from a program feed). */
export const TALKBACK_TRACK_NAME = "talkback";

/** The talkback data-channel message (separate `type` so fans/program readers ignore
 *  it — it never touches program state). Drives the talent's "Director talkback"
 *  indicator; the SFU permission is what actually gates who HEARS it. */
export interface TalkbackSignal {
  type: "talkback";
  active: boolean;
  /** "all" = every connected talent; otherwise the targeted talent's STABLE key. */
  target: "all" | string;
  by: "director";
}

/** A talkback target: everyone on the call's talent, or one specific talent (by the
 *  ADR 0042 STABLE key = participant name). */
export type TalkbackTarget = { kind: "all" } | { kind: "one"; key: string };

/** A minimal view of a talent participant for building the permission list — the
 *  STABLE key (name) plus the per-join LiveKit identity the permission API needs. */
export interface TalentRef {
  key: string;
  identity: string;
}

/** The LiveKit ParticipantTrackPermission list for a talkback target: allow ONLY the
 *  targeted talent to subscribe (everyone else — fans, the egress recorder — is
 *  excluded because setTrackSubscriptionPermissions is called with
 *  allParticipantsAllowed = false). "all" permits every talent; "one" permits just
 *  the matching stable key. A target with no live match yields an empty list (no one
 *  can subscribe — safe: talkback simply reaches nobody until they're present). */
export function talkbackPermissions(
  target: TalkbackTarget,
  talent: TalentRef[],
): { participantIdentity: string; allowAll: boolean }[] {
  const targeted = target.kind === "all" ? talent : talent.filter((t) => t.key === target.key);
  return targeted.map((t) => ({ participantIdentity: t.identity, allowAll: true }));
}

/** True when a subscribed remote publication is the Director's talkback mic — matched
 *  by the reserved track name, so a talent plays it as talkback (not as a program
 *  feed). Kept tolerant of the shapes tests/LiveKit expose. */
export function isTalkbackPublication(pub: { trackName?: string; name?: string } | undefined | null): boolean {
  if (!pub) return false;
  return pub.trackName === TALKBACK_TRACK_NAME || pub.name === TALKBACK_TRACK_NAME;
}

/** Label a target for the talent's on-screen indicator ("Director" is always the
 *  speaker; the target only affects who hears it). */
export function talkbackTargetLabel(target: TalkbackTarget, name?: string): string {
  if (target.kind === "all") return "all talent";
  return name || target.key;
}

// True when a talkback signal is meant for THIS talent (targeted directly, or "all").
export function talkbackForSelf(target: string, selfKey: string | undefined): boolean {
  return target === "all" || (!!selfKey && target === selfKey);
}

// A minimal room shape the listener needs — kept structural so a test fake works.
interface TalkbackRoom {
  on(event: string, cb: (...args: unknown[]) => void): unknown;
  off?(event: string, cb: (...args: unknown[]) => void): unknown;
}

/** Talent-side talkback listener (ADR 0213): plays the Director's private talkback
 *  audio into a hidden <audio> when it arrives, and surfaces `talkbackActive` for an
 *  on-screen indicator (driven by the Director's `{type:"talkback"}` signal, filtered
 *  to this talent's stable key or "all"). Used by BOTH the Legend Go-Live and the
 *  guest join screens. The SFU permission is what actually gates who RECEIVES the
 *  audio; the indicator just says who the Director is addressing. */
export function useTalkbackListener(
  room: TalkbackRoom | null,
  selfKey: string | undefined,
): { talkbackActive: boolean; talkbackAudioRef: React.RefObject<HTMLAudioElement | null> } {
  const [active, setActive] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    if (!room) return;
    const onData = (...args: unknown[]) => {
      try {
        const payload = args[0] as Uint8Array;
        const msg = JSON.parse(new TextDecoder().decode(payload)) as Partial<TalkbackSignal>;
        if (msg.type !== "talkback") return;
        setActive(!!msg.active && talkbackForSelf(String(msg.target ?? ""), selfKey));
      } catch {
        // ignore non-talkback / malformed data messages
      }
    };
    const attachIfTalkback = (...args: unknown[]) => {
      const track = args[0] as { attach?: (el: HTMLMediaElement) => void } | undefined;
      const pub = args[1] as { trackName?: string; name?: string } | undefined;
      if (isTalkbackPublication(pub) && track?.attach && audioRef.current) {
        try {
          track.attach(audioRef.current);
        } catch {
          // jsdom / detached element — audio is cosmetic under test
        }
      }
    };
    const detachIfTalkback = (...args: unknown[]) => {
      const track = args[0] as { detach?: () => void } | undefined;
      const pub = args[1] as { trackName?: string; name?: string } | undefined;
      if (isTalkbackPublication(pub) && track?.detach) {
        try {
          track.detach();
        } catch {
          // best-effort
        }
      }
    };
    room.on("dataReceived", onData);
    room.on("trackSubscribed", attachIfTalkback);
    room.on("trackUnsubscribed", detachIfTalkback);
    return () => {
      room.off?.("dataReceived", onData);
      room.off?.("trackSubscribed", attachIfTalkback);
      room.off?.("trackUnsubscribed", detachIfTalkback);
    };
  }, [room, selfKey]);
  return { talkbackActive: active, talkbackAudioRef: audioRef };
}
